import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Kicker } from "@/components/ui/kicker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { GithubIcon as Github } from "@/components/icons";
import { LimitReachedBanner } from "@/components/limit-reached-banner";

type CreateTab = "scratch" | "github";

function normalizeUrlInput(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

function validateUrl(value: string): string {
  if (!value.trim()) return "";
  const normalized = normalizeUrlInput(value);
  try {
    const parsed = new URL(normalized);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return "URL must use http or https";
    }
    return "";
  } catch {
    return "Enter a valid URL (e.g. example.com)";
  }
}

export function StepCreate({
  onComplete,
  isMock = false,
}: {
  onComplete: (id: string) => void;
  isMock?: boolean;
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<CreateTab>("scratch");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [selectedRepoId, setSelectedRepoId] = useState<string>("");
  const [isCreating, setIsCreating] = useState(false);

  const { data: githubRepos } = trpc.github.listRepositories.useQuery({});
  const hasGithubRepos = (githubRepos?.length ?? 0) > 0;

  const { data: planSummary } = trpc.usage.planSummary.useQuery();
  const isProjectLimitReached = Boolean(
    planSummary?.projects &&
      !planSummary.projects.unlimited &&
      planSummary.projects.used >= planSummary.projects.limit,
  );

  const createMutation = trpc.project.create.useMutation();

  const selectedRepo = githubRepos?.find((r) => r.id === selectedRepoId);

  useEffect(() => {
    if (!selectedRepo) return;
    const repoName =
      selectedRepo.fullName.split("/").pop() ?? selectedRepo.fullName;
    setName((prev) =>
      prev
        ? prev
        : repoName
            .replace(/[-_]+/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase()),
    );
    if (selectedRepo.repoDescription) {
      setDescription((prev) => prev || selectedRepo.repoDescription || "");
    }
  }, [selectedRepo]);

  function handleUrlBlur() {
    if (url.trim()) {
      setUrlError(validateUrl(url));
    } else {
      setUrlError("");
    }
  }

  function canCreate(): boolean {
    if (isCreating || !name.trim()) return false;
    if (url.trim() && validateUrl(url)) return false;
    if (tab === "github" && !selectedRepoId) return false;
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canCreate()) return;
    setIsCreating(true);

    if (isMock) {
      onComplete(crypto.randomUUID());
      setIsCreating(false);
      return;
    }

    try {
      const normalizedUrl = url.trim() ? normalizeUrlInput(url) : undefined;
      const project = await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        productUrl: normalizedUrl,
        githubRepositoryId:
          tab === "github" ? selectedRepoId || undefined : undefined,
      });

      onComplete(project.id);
    } catch (err) {
      toastTrpcError(err, "Could not create project");
    } finally {
      setIsCreating(false);
    }
  }

  if (isProjectLimitReached) {
    return (
      <div className="space-y-6 max-w-lg mx-auto py-16 text-center">
        <LimitReachedBanner resource="projects" />
        <p className="text-sm text-muted-foreground">
          You&apos;ve reached your workspace project limit. You cannot create a
          new project until existing projects are removed.
        </p>
        <Button variant="outline" onClick={() => navigate("/projects")}>
          Return to projects
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">
          Create a new project
        </h1>
        <p className="text-sm text-muted-foreground">
          Set up your project workspace, then run walkthroughs and sync findings
          from your coding agent.
        </p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as CreateTab)}
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="scratch">Start from scratch</TabsTrigger>
          <TabsTrigger value="github">
            <Github className="mr-1.5 h-3.5 w-3.5" />
            Import from GitHub
          </TabsTrigger>
        </TabsList>

        <TabsContent value="github" className="space-y-6">
          {hasGithubRepos ? (
            <div className="space-y-2">
              <Kicker>
                <Label htmlFor="wizard-github-repo">Repository</Label>
              </Kicker>
              <Select value={selectedRepoId} onValueChange={setSelectedRepoId}>
                <SelectTrigger id="wizard-github-repo" className="w-full">
                  <SelectValue placeholder="Select a repository" />
                </SelectTrigger>
                <SelectContent>
                  {githubRepos?.map((repo) => (
                    <SelectItem key={repo.id} value={repo.id}>
                      {repo.fullName}
                      {repo.isPrivate ? " (Private)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed p-6 space-y-3">
              <p className="text-sm text-muted-foreground">
                No GitHub repositories connected yet. Connect your GitHub
                installation in workspace settings to import a repository.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate("/settings?tab=integrations")}
              >
                <Github className="mr-2 h-4 w-4" />
                Connect GitHub
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="scratch" />
      </Tabs>

      <div className="space-y-6">
        <div className="space-y-2">
          <Kicker>
            <Label htmlFor="project-name">Project name</Label>
          </Kicker>
          <Input
            id="project-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Agricultural Grant Portal"
            autoFocus
            required
          />
        </div>

        <div className="space-y-2">
          <Kicker>
            <Label htmlFor="project-description">
              Description{" "}
              <span className="text-muted-foreground">(optional)</span>
            </Label>
          </Kicker>
          <Textarea
            id="project-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this product, who is it for, and what are its key user journeys?"
            rows={4}
          />
        </div>

        <div className="space-y-2">
          <Kicker>
            <Label htmlFor="seed-url">
              App URL <span className="text-muted-foreground">(optional)</span>
            </Label>
          </Kicker>
          <Input
            id="seed-url"
            type="text"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              if (urlError) setUrlError(validateUrl(e.target.value));
            }}
            onBlur={handleUrlBlur}
            placeholder="https://app.example.com"
            aria-invalid={!!urlError}
          />
          {urlError && <p className="text-xs text-destructive">{urlError}</p>}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 border-t border-border/60 pt-6">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate("/projects")}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!canCreate()}>
          {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create project
        </Button>
      </div>
    </form>
  );
}
